import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import {
	getSearchPageProactiveAiMultiVariantCohort,
	type SearchPageProactiveAiMultiVariantCohort,
} from '../../common/utils/experiments';
import { useSearchQuery } from '../store/search';

/**
 * Shared accessor for the `search_page_proactive_ai_multi_variant` experiment.
 *
 * Exposure semantics: the experiment is exposed the FIRST time the user lands
 * on full-page search with a non-empty term, regardless of whether SAIN
 * renders an answer (other variant legs may be eligible while SAIN exists, so
 * exposure must NOT be gated on SAIN).
 *
 * Exposure must fire exactly once per page instance and from a single place,
 * so every variant controller can read the resolved cohort WITHOUT each one
 * writing its own reading logic and risking duplicate / inconsistent
 * exposures.
 *
 * The resolved cohort is held in React state scoped to the provider instance
 * (NOT module scope) so it is SSR-safe (each request/page instance has its own
 * value), reactive (read-only consumers re-render when it resolves), and the
 * exposure fires from a commit-tied effect rather than during render.
 */
const SearchPageProactiveAiCohortContext = createContext<
	SearchPageProactiveAiMultiVariantCohort | undefined
>(undefined);

/**
 * Provider that owns the experiment exposure. Mount once, high in the
 * search-page tree, so exposure fires on non-empty-term landing independently
 * of any individual variant's render lifecycle.
 */
export const SearchPageProactiveAiExperimentProvider = ({
	children,
}: {
	children?: React.ReactNode;
}): React.JSX.Element => {
	const [searchQuery] = useSearchQuery();
	const hasNonEmptyTerm = searchQuery.trim().length > 0;

	const [cohort, setCohort] = useState<SearchPageProactiveAiMultiVariantCohort | undefined>(
		undefined,
	);
	const hasFiredExposureRef = useRef(false);

	useEffect(() => {
		if (hasFiredExposureRef.current || !hasNonEmptyTerm) {
			return;
		}
		// Resolve + fire exposure exactly once, the first time the term is
		// non-empty. The guard is set only AFTER a successful resolve so a
		// transient throw does not permanently latch the feature off.
		const resolved = getSearchPageProactiveAiMultiVariantCohort({
			fireExperimentExposure: true,
		});
		hasFiredExposureRef.current = true;
		setCohort(resolved);
	}, [hasNonEmptyTerm]);

	return (
		<SearchPageProactiveAiCohortContext.Provider value={cohort}>
			{children}
		</SearchPageProactiveAiCohortContext.Provider>
	);
};

/**
 * Read-only accessor for the resolved cohort. Returns `undefined` until the
 * provider has fired the exposure (i.e. until the user has landed with a
 * non-empty term). Never fires an exposure itself.
 */
export const useSearchPageProactiveAiCohort = ():
	| SearchPageProactiveAiMultiVariantCohort
	| undefined => useContext(SearchPageProactiveAiCohortContext);
